import type { CSSProperties } from 'react';
import {
  SIDEBAR_WIDTH,
  TARGET_WIDTH,
  TARGET_WIDTH_MULTI,
  PORT_WIDTH,
  PORT_WIDTH_MULTI,
  REPLAY_WIDTH,
} from './panelConstants';

const NUM_COLS = 6;
const NUM_ROWS = 3;

export interface GridStylesInput {
  tableExpanded: boolean;
  sidebarActive: boolean;
  replayActive: boolean;
  targetPanelActive: boolean;
  portPanelActive: boolean;
  multipleTargets: boolean;
  multiplePorts: boolean;
  targetCardExpanded: boolean;
  drawingMode: boolean;
  tableHeight: number;
}

export interface GridStyles {
  layerController: CSSProperties;
  followShip: CSSProperties;
  metadata: CSSProperties;
  reportLegend: CSSProperties;
  reportTable: CSSProperties;
  targetInfo: CSSProperties;
  portInfo: CSSProperties;
  replay: CSSProperties;
  mapPanel: CSSProperties;
}

interface PanelDef {
  name: string;
  width: string;
  rowStart: number;
  rowEnd: number;
}

/**
 * Builds a grid-template-areas CSS string from the computed panel positions.
 *
 * The resulting template is a visual ASCII map of the 6×3 grid, e.g.:
 *   "search   .      legend legend legend panel"
 *   ".        .      .      .      .      panel"
 *   "controls .      .      .      .      panel"
 *
 * Elements that overlap with other areas (metadata, reportTable, followShip)
 * use explicit gridColumn/gridRow instead of named areas.
 */
function buildGridTemplateAreas(
  panels: Array<PanelDef & { col: number }>,
  controlsRow: number,
  legendColEnd: number,
): string {
  const rows: string[] = [];

  for (let row = 1; row <= NUM_ROWS; row++) {
    const cells: string[] = new Array(NUM_COLS).fill('.');

    if (row === 1) cells[0] = 'search';
    if (row === controlsRow) cells[0] = 'controls';

    // Legend spans row 1 from col 3 up to (but not including) the first panel in row 1
    if (row === 1) {
      for (let c = 3; c < legendColEnd; c++) {
        cells[c - 1] = 'legend';
      }
    }

    for (const panel of panels) {
      if (row >= panel.rowStart && row < panel.rowEnd) {
        cells[panel.col - 1] = panel.name;
      }
    }

    rows.push(`"${cells.join(' ')}"`);
  }

  return rows.join(' ');
}

function normalizeForDrawingMode(input: GridStylesInput): {
  sidebarActive: boolean;
  replayActive: boolean;
  targetPanelActive: boolean;
  portPanelActive: boolean;
  tableExpanded: boolean;
} {
  if (input.drawingMode) {
    return {
      sidebarActive: false,
      replayActive: false,
      targetPanelActive: false,
      portPanelActive: false,
      tableExpanded: false,
    };
  }
  return {
    sidebarActive: input.sidebarActive,
    replayActive: input.replayActive,
    targetPanelActive: input.targetPanelActive,
    portPanelActive: input.portPanelActive,
    tableExpanded: input.tableExpanded,
  };
}

function buildRightPanels(
  input: GridStylesInput,
  active: ReturnType<typeof normalizeForDrawingMode>,
): PanelDef[] {
  const rightPanels: PanelDef[] = [];

  if (active.sidebarActive) {
    rightPanels.push({
      name: 'panel',
      width: `${SIDEBAR_WIDTH}px`,
      rowStart: 1,
      rowEnd: 4,
    });
  }

  const targetDef: PanelDef | null = active.targetPanelActive
    ? {
        name: 'target',
        width: `${input.multipleTargets ? TARGET_WIDTH_MULTI : TARGET_WIDTH}px`,
        rowStart: 1,
        rowEnd: input.targetCardExpanded ? 4 : 2,
      }
    : null;

  // Expanded target before port; collapsed target after port.
  if (targetDef && input.targetCardExpanded) {
    rightPanels.push(targetDef);
  }

  if (active.portPanelActive) {
    rightPanels.push({
      name: 'port',
      width: `${input.multiplePorts ? PORT_WIDTH_MULTI : PORT_WIDTH}px`,
      rowStart: 1,
      rowEnd: 4,
    });
  }

  if (targetDef && !input.targetCardExpanded) {
    rightPanels.push(targetDef);
  }

  if (active.replayActive) {
    rightPanels.push({
      name: 'replay',
      width: `${REPLAY_WIDTH}px`,
      rowStart: active.tableExpanded ? 2 : 3,
      rowEnd: active.tableExpanded ? 3 : 4,
    });
  }

  return rightPanels;
}

function assignPanelColumns(
  rightPanels: PanelDef[],
): Array<PanelDef & { col: number }> {
  const panels: Array<PanelDef & { col: number }> = [];
  for (const p of rightPanels) {
    let col = NUM_COLS;
    while (col > 0) {
      const hasConflict = panels.some(
        (existing) =>
          existing.col === col &&
          p.rowStart < existing.rowEnd &&
          existing.rowStart < p.rowEnd,
      );
      if (!hasConflict) break;
      col--;
    }
    panels.push({ ...p, col });
  }
  return panels;
}

function computeColumnWidths(
  panels: Array<PanelDef & { col: number }>,
): string[] {
  const colWidths = ['auto', '1fr', 'auto', 'auto', 'auto', 'auto'];
  for (const panel of panels) {
    const idx = panel.col - 1;
    const current = colWidths[idx];
    if (current === 'auto' || current === '1fr') {
      colWidths[idx] = panel.width;
    } else {
      const currentPx = parseInt(current, 10);
      const panelPx = parseInt(panel.width, 10);
      if (panelPx > currentPx) {
        colWidths[idx] = panel.width;
      }
    }
  }
  return colWidths;
}

/**
 * Pure function that computes CSS grid positioning styles for each MapPanel area.
 *
 * Uses grid-template-areas for self-documenting layouts. Most elements are positioned
 * via named areas (`gridArea: 'name'`). Table and metadata use explicit
 * gridColumn/gridRow because grid-template-areas requires each cell to belong to
 * exactly one named area, and these elements share cells with other areas when
 * the table is expanded.
 */
export function computeGridStyles(input: GridStylesInput): GridStyles {
  const active = normalizeForDrawingMode(input);
  const controlsRow = active.tableExpanded ? 2 : 3;

  // --- Right panel list (rightmost-first ordering) ---
  const rightPanels = buildRightPanels(input, active);
  const panels = assignPanelColumns(rightPanels);
  const colWidths = computeColumnWidths(panels);

  // --- Legend: spans row 1 up to the leftmost panel that occupies row 1 ---
  const panelsInRow1 = panels.filter((p) => p.rowStart === 1);
  const leftmostRow1Panel = panelsInRow1.reduce(
    (min, p) => Math.min(min, p.col),
    NUM_COLS + 1,
  );

  // --- Metadata (explicit placement — avoids full-height panels) ---
  let mdColEnd = NUM_COLS + 1;
  let mdRowStart = 3;
  let mdRowEnd = 4;
  if (active.tableExpanded || active.replayActive) {
    mdRowStart--;
    mdRowEnd--;
  }
  if (active.replayActive && active.tableExpanded) mdColEnd--;
  if (active.sidebarActive) mdColEnd--;
  if (active.targetPanelActive && input.targetCardExpanded) mdColEnd--;
  if (active.portPanelActive) mdColEnd--;

  // --- Report table (explicit placement — overlaps with controls, metadata, replay) ---
  let rtColEnd = NUM_COLS + 1;
  if (active.sidebarActive) rtColEnd--;
  if (active.targetPanelActive && input.targetCardExpanded) rtColEnd--;
  if (active.portPanelActive) rtColEnd--;

  // --- Follow ship indicator (explicit placement — overlaps with controls row) ---
  const panelsInControlsRow = panels.filter(
    (p) => controlsRow >= p.rowStart && controlsRow < p.rowEnd,
  );
  const followColEnd = panelsInControlsRow.reduce(
    (min, p) => Math.min(min, p.col),
    NUM_COLS + 1,
  );

  // --- Build grid template areas ---
  const gridTemplateAreas = buildGridTemplateAreas(
    panels,
    controlsRow,
    leftmostRow1Panel,
  );

  return {
    layerController: { gridArea: 'controls' },
    followShip: {
      gridColumn: `1 / ${followColEnd}`,
      gridRow: `${controlsRow} / ${controlsRow + 1}`,
    },
    metadata: {
      gridColumn: `span 1 / ${mdColEnd}`,
      gridRow: `${mdRowStart} / ${mdRowEnd}`,
    },
    reportLegend: { gridArea: 'legend' },
    reportTable: {
      gridColumn: `1 / ${rtColEnd}`,
      gridRow: '2 / 4',
    },
    targetInfo: {
      gridArea: 'target',
      ...(input.targetCardExpanded ? {} : { justifySelf: 'end' as const }),
    },
    portInfo: { gridArea: 'port' },
    replay: { gridArea: 'replay' },
    mapPanel: {
      display: 'grid',
      gridTemplateColumns: colWidths.join(' '),
      gridTemplateRows: `auto 1fr ${active.tableExpanded ? `${input.tableHeight}px` : 'auto'}`,
      gridTemplateAreas,
    },
  };
}
